import { translateKey, useTranslation } from "../../i18n";
import { CircleAlert, CircleCheck, Info, LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ToastTone = "info" | "success" | "error" | "loading";

interface ToastOptions {
  id?: string;
  description?: string;
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
  title: string;
  tone: ToastTone;
  revision: number;
}

let items: ToastItem[] = [];
let sequence = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function show(tone: ToastTone, title: string, options: ToastOptions = {}): string {
  const id = options.id ?? `toast-${++sequence}`;
  const previous = items.find(item => item.id === id);
  const next: ToastItem = { ...options, id, title, tone, revision: (previous?.revision ?? 0) + 1 };
  items = [next, ...items.filter(item => item.id !== id)].slice(0, 5);
  emit();
  return id;
}

export const toast = {
  info: (title: string, options?: ToastOptions) => show("info", title, options),
  success: (title: string, options?: ToastOptions) => show("success", title, options),
  error: (title: string, options?: ToastOptions) => show("error", title, options),
  loading: (title: string, options?: ToastOptions) => show("loading", title, options),
  dismiss: (id?: string) => {
    items = id ? items.filter(item => item.id !== id) : [];
    emit();
  },
};

const TONE_ICON = {
  info: Info,
  success: CircleCheck,
  error: CircleAlert,
  loading: LoaderCircle,
} satisfies Record<ToastTone, typeof Info>;

export function Toaster() {
  useTranslation();
  const [visible, setVisible] = useState(items);
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const update = () => setVisible([...items]);
    listeners.add(update);
    update();
    return () => { listeners.delete(update); };
  }, []);

  useEffect(() => {
    // 通知固定挂到 body，脱离页面布局与祖先层叠上下文（toast z-index 200 高于共享弹层 z-50）。
    setHost(document.body);
  }, []);

  // 自动根据 duration 销毁
  useEffect(() => {
    if (!visible.length) return;
    const timers = visible.map(item => {
      const ms = item.duration ?? (item.tone === "loading" ? 86_400_000 : 5_000);
      return setTimeout(() => {
        toast.dismiss(item.id);
      }, ms);
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, [visible]);

  const content = (
    <div className="toast-viewport">
      {visible.map(item => {
        const Icon = TONE_ICON[item.tone];
        return (
          <div
            className="toast-root"
            data-tone={item.tone}
            key={`${item.id}-${item.revision}`}
            role={item.tone === "error" ? "alert" : "status"}
          >
            <Icon aria-hidden="true" className={item.tone === "loading" ? "toast-spinner" : undefined} size={18} />
            <div className="toast-copy">
              <div className="toast-title">{item.title}</div>
              {item.description ? <div className="toast-description">{item.description}</div> : null}
            </div>
            <button
              aria-label={translateKey("workspace.closeToast")}
              className="toast-close"
              onClick={() => toast.dismiss(item.id)}
              type="button"
            >
              <X aria-hidden="true" size={15} />
            </button>
          </div>
        );
      })}
    </div>
  );

  if (!host) return content;
  return createPortal(content, host);
}

